// safeJsonParse.ts
export function safeJsonParse<T = any>(input: unknown): T | null {
  try {
    // 0) Якщо вже об'єкт — повертай як є
    if (input && typeof input === 'object') {
      return input as T;
    }

    // 1) Базові перевірки
    if (typeof input !== 'string') return null;
    const src = input.trim();
    if (!src) return null;

    // 2) Швидка перевірка на "обрізаний" JSON (баланс дужок/квадр. дужок)
    if (!isLikelyCompleteJson(src)) {
      console.error('❌ Looks like truncated/unfinished JSON string');
      return null;
    }

    // 3) Перетворення ТІЛЬКИ числових токенів поза рядками (>= 15 цифр)
    const sanitized = stringifyBigIntsOutsideStrings(src);

    // 4) Парсимо
    return JSON.parse(sanitized);
  } catch (error) {
    // Покажи невеликий фрагмент для дебага, щоб не засмічувати лог
    const preview = typeof input === 'string' ? input.slice(0, 200) : String(input);
    console.error('❌ JSON parse error:', error, '\nPreview:', preview);
    return null;
  }
}

/**
 * Обгортка тільки чисел поза рядками (>= 15 цифр) у лапки.
 * Працює в один прохід O(n), з урахуванням escape-послідовностей.
 */
function stringifyBigIntsOutsideStrings(src: string): string {
  let out = '';
  let i = 0;
  let inStr = false;
  let escaped = false;

  while (i < src.length) {
    const ch = src[i];

    if (inStr) {
      out += ch;
      if (escaped) {
        escaped = false; // цей символ екранований – просто пропускаємо
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inStr = false;
      }
      i++;
      continue;
    }

    // не в рядку
    if (ch === '"') {
      inStr = true;
      out += ch;
      i++;
      continue;
    }

    // якщо тут починається числовий токен (мінус + цифра або цифра)
    if ((ch === '-' && isDigit(src[i + 1])) || isDigit(ch)) {
      const start = i;
      let j = i;

      // захоплюємо простий цілий токен: [ - ]? [0-9]+
      if (src[j] === '-') j++;
      const digitsStart = j;
      while (j < src.length && isDigit(src[j])) j++;

      const numToken = src.slice(start, j);
      const digitsOnly = src.slice(digitsStart, j);

      // Обгортаємо ТІЛЬКИ якщо це довга ціла послідовність (>= 15 цифр)
      if (digitsOnly.length >= 15) {
        out += `"${numToken}"`;
      } else {
        out += numToken;
      }

      i = j;
      continue;
    }

    // інші символи — як є
    out += ch;
    i++;
  }

  return out;
}

function isDigit(c: string | undefined): boolean {
  return !!c && c >= '0' && c <= '9';
}

/**
 * Груба, але швидка евристика "чи схоже це на завершений JSON":
 * - парність лапок (з урахуванням escape) та баланс {}, []
 */
function isLikelyCompleteJson(src: string): boolean {
  let inStr = false;
  let escaped = false;
  let brace = 0;
  let bracket = 0;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inStr) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inStr = false;
      }
      continue;
    } else {
      if (ch === '"') inStr = true;
      else if (ch === '{') brace++;
      else if (ch === '}') brace--;
      else if (ch === '[') bracket++;
      else if (ch === ']') bracket--;
    }
    // раннє падіння нижче нуля означає явну поломку
    if (brace < 0 || bracket < 0) return false;
  }

  // усі дужки збалансовані і не всередині рядка
  return !inStr && brace === 0 && bracket === 0;
}
